import React, { useState, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { FileCheck, Upload, AlertCircle, CheckCircle, Clock, Camera, FileText, Home, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { CameraSelfie } from './CameraSelfie';

export const KYCSection = () => {
  const { user, api, refreshUser } = useAuth();
  const [documents, setDocuments] = useState({
    id_document: null,
    proof_of_address: null,
    selfie: null
  });
  const [previews, setPreviews] = useState({
    id_document: null,
    proof_of_address: null,
    selfie: null
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const idInputRef = useRef(null);
  const addressInputRef = useRef(null);

  const handleFileSelect = (type, file) => {
    if (file) {
      setDocuments(prev => ({ ...prev, [type]: file }));
      const previewUrl = URL.createObjectURL(file);
      setPreviews(prev => ({ ...prev, [type]: previewUrl }));
    }
  };

  const removeFile = (type) => {
    setDocuments(prev => ({ ...prev, [type]: null }));
    if (previews[type]) {
      URL.revokeObjectURL(previews[type]);
    }
    setPreviews(prev => ({ ...prev, [type]: null }));
    if (type === 'selfie') {
      setShowCamera(false);
    }
  };

  const handleSelfieCapture = (file, dataUrl) => {
    setDocuments(prev => ({ ...prev, selfie: file }));
    setPreviews(prev => ({ ...prev, selfie: dataUrl }));
    setShowCamera(false);
  };

  const handleSubmit = async () => {
    if (!documents.id_document || !documents.proof_of_address || !documents.selfie) {
      toast.error('Please upload all required documents including your selfie');
      return;
    }
    
    setLoading(true);
    try {
      // Submit all KYC documents at once
      await api.post('/kyc/submit-all');
      setSubmitted(true);
      await refreshUser();
      toast.success('KYC documents submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    switch (user?.kyc_status) {
      case 'approved':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'rejected':
        return <AlertCircle className="w-16 h-16 text-red-500" />;
      case 'submitted':
        return <Clock className="w-16 h-16 text-yellow-500" />;
      default:
        return <FileCheck className="w-16 h-16 text-gray-400" />;
    }
  };

  // Success screen after submission
  if (submitted || user?.kyc_status === 'submitted') {
    return (
      <div className="space-y-6" data-testid="kyc-section">
        <h1 className="font-syne text-2xl font-bold text-gray-900">KYC Verification</h1>

        <Card className="bg-white border border-gray-200 p-8 text-center rounded-xl shadow-sm">
          <div className="mb-6">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="font-syne text-2xl font-bold text-gray-900 mb-3">Thank You for the KYC!</h2>
            <p className="text-gray-600 text-lg mb-2">
              Your "Know Your Customer" documentation has been received.
            </p>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6 max-w-md mx-auto">
            <p className="text-blue-900 font-semibold text-base mb-1">
              Our admin will review and update your status.
            </p>
            <p className="text-blue-700 text-sm">
              You will be notified once your verification is complete. This usually takes 1-2 business days.
            </p>
          </div>

          <div className="flex items-center justify-center gap-2 text-gray-500">
            <Clock className="w-5 h-5" />
            <span>Status: Under Review</span>
          </div>
        </Card>

        {/* What happens next */}
        <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
          <h2 className="font-syne text-lg font-bold text-gray-900 mb-4">What happens next?</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                <span className="text-blue-600 font-bold">1</span>
              </div>
              <p className="text-gray-700 font-medium">Document Review</p>
              <p className="text-gray-500 text-sm">Our admin team verifies your documents</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                <span className="text-blue-600 font-bold">2</span>
              </div>
              <p className="text-gray-700 font-medium">Identity Confirmation</p>
              <p className="text-gray-500 text-sm">We confirm your identity and selfie</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                <span className="text-blue-600 font-bold">3</span>
              </div>
              <p className="text-gray-700 font-medium">Full Access</p>
              <p className="text-gray-500 text-sm">Unlock loans, insurance & higher limits</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Approved status screen
  if (user?.kyc_status === 'approved') {
    return (
      <div className="space-y-6" data-testid="kyc-section">
        <h1 className="font-syne text-2xl font-bold text-gray-900">KYC Verification</h1>
        <Card className="bg-white border border-green-200 p-8 text-center rounded-xl shadow-sm">
          <div className="mb-6">
            {getStatusIcon()}
            <h2 className="font-syne text-2xl font-bold text-green-700 mt-4 mb-2">KYC Verified</h2>
            <p className="text-gray-600">Your identity has been verified. You have full access to all features.</p>
          </div>
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Verified Account</span>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kyc-section">
      <h1 className="font-syne text-2xl font-bold text-gray-900">KYC Verification</h1>

      {/* Status Card */}
      {user?.kyc_status === 'rejected' && (
        <Card className="bg-white border border-red-200 p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-4">
            <AlertCircle className="w-10 h-10 text-red-500" />
            <div>
              <h2 className="font-syne text-xl font-bold text-red-600">KYC Rejected</h2>
              <p className="text-gray-500">Your documents were rejected. Please resubmit with valid documents.</p>
            </div>
          </div>
        </Card>
      )}

      {user?.kyc_status === 'pending' && (
        <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-4">
            <FileCheck className="w-10 h-10 text-gray-400" />
            <div>
              <h2 className="font-syne text-xl font-bold text-gray-700">Verification Required</h2>
              <p className="text-gray-500">Complete your KYC to access loans and insurance features.</p>
            </div>
          </div>
        </Card>
      )}

      {/* Upload Form */}
      <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
        <h2 className="font-syne text-lg font-bold text-gray-900 mb-2">Upload Documents</h2>
        <p className="text-gray-500 text-sm mb-6">
          Please upload clear photos of your documents and take a selfie for verification.
        </p>
        
        <div className="grid md:grid-cols-3 gap-6">
          {/* ID Document */}
          <div className="space-y-3">
            <input
              type="file"
              ref={idInputRef}
              onChange={(e) => handleFileSelect('id_document', e.target.files?.[0])}
              accept="image/*,.pdf"
              className="hidden"
              data-testid="kyc-id-file-input"
            />
            
            {previews.id_document ? (
              <div className="relative w-full aspect-square bg-gray-100 rounded-xl overflow-hidden">
                <img 
                  src={previews.id_document} 
                  alt="ID Document" 
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => removeFile('id_document')}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                  data-testid="kyc-id-remove-btn"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute bottom-2 left-2 right-2 bg-green-600 text-white text-xs text-center py-1 rounded-lg">
                  Uploaded
                </div>
              </div>
            ) : (
              <button
                onClick={() => idInputRef.current?.click()}
                className="w-full aspect-square bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center p-4 hover:border-afriperx-gold hover:bg-red-50/30 transition-colors"
                data-testid="kyc-id-upload-btn"
              >
                <FileText className="w-12 h-12 text-gray-400 mb-3" />
                <p className="text-gray-700 font-medium text-center">ID Document</p>
                <p className="text-gray-400 text-xs text-center mt-1">Passport, ID Card, or Driver's License</p>
                <div className="mt-3 flex items-center gap-1 text-afriperx-gold text-sm">
                  <Upload className="w-4 h-4" />
                  <span>Click to upload</span>
                </div>
              </button>
            )}
          </div>

          {/* Proof of Address */}
          <div className="space-y-3">
            <input
              type="file"
              ref={addressInputRef}
              onChange={(e) => handleFileSelect('proof_of_address', e.target.files?.[0])}
              accept="image/*,.pdf"
              className="hidden"
              data-testid="kyc-address-file-input"
            />
            
            {previews.proof_of_address ? (
              <div className="relative w-full aspect-square bg-gray-100 rounded-xl overflow-hidden">
                <img 
                  src={previews.proof_of_address} 
                  alt="Proof of Address" 
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => removeFile('proof_of_address')}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                  data-testid="kyc-address-remove-btn"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute bottom-2 left-2 right-2 bg-green-600 text-white text-xs text-center py-1 rounded-lg">
                  Uploaded
                </div>
              </div>
            ) : (
              <button
                onClick={() => addressInputRef.current?.click()}
                className="w-full aspect-square bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center p-4 hover:border-afriperx-gold hover:bg-red-50/30 transition-colors"
                data-testid="kyc-address-upload-btn"
              >
                <Home className="w-12 h-12 text-gray-400 mb-3" />
                <p className="text-gray-700 font-medium text-center">Proof of Address</p>
                <p className="text-gray-400 text-xs text-center mt-1">Utility Bill or Bank Statement</p>
                <div className="mt-3 flex items-center gap-1 text-afriperx-gold text-sm">
                  <Upload className="w-4 h-4" />
                  <span>Click to upload</span>
                </div>
              </button>
            )}
          </div>

          {/* Selfie - Camera Option */}
          <div className="space-y-3">
            {previews.selfie ? (
              <div className="relative w-full aspect-square bg-gray-100 rounded-xl overflow-hidden">
                <img 
                  src={previews.selfie} 
                  alt="Selfie" 
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => removeFile('selfie')}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                  data-testid="kyc-selfie-remove-btn"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute bottom-2 left-2 right-2 bg-green-600 text-white text-xs text-center py-1 rounded-lg">
                  Selfie captured
                </div>
              </div>
            ) : !showCamera ? (
              <button
                onClick={() => setShowCamera(true)}
                className="w-full aspect-square bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center p-4 hover:border-afriperx-gold hover:bg-red-50/30 transition-colors"
                data-testid="kyc-selfie-start-btn"
              >
                <Camera className="w-12 h-12 text-gray-400 mb-3" />
                <p className="text-gray-700 font-medium text-center">Take Selfie</p>
                <p className="text-gray-400 text-xs text-center mt-1">Use your camera to take a selfie</p>
                <div className="mt-3 flex items-center gap-1 text-afriperx-gold text-sm">
                  <Camera className="w-4 h-4" />
                  <span>Open camera</span>
                </div>
              </button>
            ) : null}
          </div>
        </div>

        {/* Camera selfie component - shown below when active */}
        {showCamera && !previews.selfie && (
          <div className="mt-6">
            <CameraSelfie
              onCapture={handleSelfieCapture}
              onCancel={() => setShowCamera(false)}
            />
          </div>
        )}

        {/* Submit button */}
        <div className="mt-6">
          <Button 
            onClick={handleSubmit}
            disabled={loading || !documents.id_document || !documents.proof_of_address || !documents.selfie}
            className="bg-afriperx-green hover:bg-afriperx-green-dark text-white w-full md:w-auto px-8"
            data-testid="kyc-submit-btn"
          >
            {loading ? 'Submitting...' : 'Submit for Verification'}
          </Button>
          {(!documents.id_document || !documents.proof_of_address || !documents.selfie) && (
            <p className="text-gray-400 text-xs mt-2">
              Upload all 3 documents to submit ({[
                !documents.id_document && 'ID Document',
                !documents.proof_of_address && 'Proof of Address',
                !documents.selfie && 'Selfie'
              ].filter(Boolean).join(', ')} remaining)
            </p>
          )}
        </div>
      </Card>

      {/* Benefits of KYC */}
      <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
        <h2 className="font-syne text-lg font-bold text-gray-900 mb-4">Why Verify?</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <CheckCircle className="w-6 h-6 text-green-500 mb-2" />
            <p className="text-gray-700 font-medium">Access Loans</p>
            <p className="text-gray-500 text-sm">Apply for personal and home loans</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <CheckCircle className="w-6 h-6 text-green-500 mb-2" />
            <p className="text-gray-700 font-medium">Insurance</p>
            <p className="text-gray-500 text-sm">Get covered with our insurance plans</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <CheckCircle className="w-6 h-6 text-green-500 mb-2" />
            <p className="text-gray-700 font-medium">Higher Limits</p>
            <p className="text-gray-500 text-sm">Unlock higher transaction limits</p>
          </div>
        </div>
      </Card>
    </div>
  );
};
